import type React from 'react';
import { useEffect, useRef } from 'react';
import type Player from 'video.js/dist/types/player';
import videojs from 'video.js';
import './Video.scss';

export interface VideoSources {
  hls: string;
  poster: string;
  regular: string;
}

export interface VideoPlayerOptions {
  aspectRatio?: string;
  autoplay?: boolean;
  controls?: boolean;
  fluid?: boolean;
  loop?: boolean;
  muted?: boolean;
  responsive?: boolean;
}

interface VideoProps extends VideoPlayerOptions {
  videoSources: VideoSources;
}

const Video: React.FC<VideoProps> = ({
  aspectRatio = '16:9',
  autoplay = false,
  controls = true,
  fluid = true,
  loop = false,
  muted = false,
  responsive = true,
  videoSources,
}) => {
  const videoElement = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoElementClone = useRef<HTMLVideoElement | null>(null);
  const preload = autoplay ? 'auto' : 'none';

  useEffect(() => {
    if (playerRef.current) {
      return;
    }

    if (videoElement.current) {
      videoElementClone.current = videoElement.current.cloneNode(
        true,
      ) as HTMLVideoElement;

      playerRef.current = videojs(videoElement.current, {
        aspectRatio,
        autoplay,
        controls,
        fluid,
        loop,
        muted,
        preload,
        responsive,
      });
    }
  }, [
    aspectRatio,
    autoplay,
    controls,
    fluid,
    loop,
    muted,
    preload,
    responsive,
    videoElement,
    videoSources,
  ]);

  useEffect(() => {
    const player = playerRef.current;
    const currentContainer = containerRef.current;
    const currentVideoElementClone = videoElementClone.current;

    return () => {
      if (player) {
        player.dispose();
        playerRef.current = null;

        // The original video element needs to be restored since the .dispose function detaches it
        // This should only occur on dev, it is being worked on: https://github.com/videojs/video.js/issues/7746
        if (currentContainer && currentVideoElementClone) {
          currentContainer.appendChild(currentVideoElementClone);
          videoElement.current = currentVideoElementClone;
        }
      }
    };
  }, [playerRef]);

  useEffect(() => {
    const player = playerRef.current;
    if (autoplay && player) {
      player.play()?.catch(() => null);
    }
  }, [autoplay]);

  if (!videoSources.regular && !videoSources.hls) {
    return null;
  }

  /**
   * We don't set the autoplay value on the video element, as that will cause chrome to start
   * playing the video with sound, as the muted is then ignored.
   *
   * Instead, we don't autoplay the video, and let the `videojs` player element handle that.
   * Because the video would already be playing before the videojs could mute it, there would be
   * sound on chrome.
   *
   * See ticket: YW-16312
   *
   * https://stackoverflow.com/questions/47638344/muted-autoplay-in-chrome-still-not-working
   */
  return (
    <div
      ref={containerRef}
      style={{ display: 'flex', flex: '1 0 100%' }}
    >
      <video
        controls={controls}
        loop={loop}
        muted={muted}
        preload={preload}
        playsInline={true}
        data-testid="video"
        className="video-js vjs-matrix"
        poster={videoSources.poster}
        ref={videoElement}
      >
        <source
          data-testid="video-source-hls"
          src={videoSources.hls}
          type="application/x-mpegURL"
        />
        <source
          data-testid="video-source-mp4"
          src={videoSources.regular}
          type="video/mp4"
        />
      </video>
    </div>
  );
};
export default Video;
